import React, { useState, useEffect } from "react";
import Plaid from "Components/FundingSources/Plaid";
import Manual from "Components/FundingSources/Manual";
import Loading from "Components/Loading";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import useRedirectUrl from "Utils/Hooks/useRedirectUrl";
import useOAuthStateId from "Utils/Hooks/useOAuthStateId";
import useAppKey from "Utils/Hooks/useAppKey";
import useSession from "Utils/Hooks/useSession";
import CancelButton from "Components/CancelButton";
import zerobalanceIcon from "Assets/Icons/zerobalance.svg";
import Finicity from "./Finicity";
import { faro, LogLevel } from "@grafana/faro-react";
import { useHistory } from "react-router-dom";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import { Button, Typography } from "@pav/pav-products-design-system";
import lockClock from "Assets/Icons/lock-clock.svg";
import StatusUpdateCard from "Components/StatusUpdateCard";
import { Box } from "@mui/material";
import "./FundingSource.scss";

const FundingSourceList = (props) => {
  const { session } = useSession();
  const history = useHistory();
  const vipApi = useVipApiFetcher();
  const [isLoading, setIsLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(false);
  const url = useRedirectUrl();
  const appKey = useAppKey();
  const oauth_state_id = useOAuthStateId();
  const [receivedRedirectUri, setReceivedRedirectUri] = useState(null);
  const [connectUrl, setConnectUrl] = useState(null);
  const [hasVerifiedBank, setHasVerifiedBanks] =
    useSessionStorage("has-verified-bank");
  const [unverifiedBank, setUnverifiedBank] = useSessionStorage(
    "unverifiedbank",
    false,
  );
  const verifyMsg =
    !props.withdraw &&
    props.noWarranty &&
    props.operatorBoth &&
    !unverifiedBank &&
    !hasVerifiedBank;

  const doNotVerify = () => {
    setUnverifiedBank(true);
    faro.api.pushLog(
      ["Nowarrany Deposit and patron declined to connect a verified account"],
      { level: LogLevel.INFO },
    );
    history.push({ pathname: "/addFunds" });
  };

  useEffect(() => {
    faro.api.pushEvent("User Adding Funding Source");
    if (props.plaidRedirect) {
      setLinkToken(localStorage.getItem("link_token"));
      setReceivedRedirectUri(
        url
          ? url + "?oauth_state_id=" + oauth_state_id
          : window.location.href.includes("oauth_state_id=")
            ? window.location.href
            : window.location.href + "?oauth_state_id=" + oauth_state_id,
      );
      // setReceivedRedirectUri("https://cdn.plaid.com/link/v2/stable/oauth.html?oauth_state_id=" + oauth_state_id);
      return;
    }

    let connectUrlReceived = false;
    (async () => {
      // Get Finicity Connect URL if Finicity is enabled
      if (props.finicityEnabled) {
        try {
          const response = await vipApi.fetch("/api/finicity/getUrl");
          if (response.status === 200) {
            const finicityConnectUrl = await response.json();
            if (finicityConnectUrl) {
              setConnectUrl(finicityConnectUrl);
              connectUrlReceived = finicityConnectUrl ? true : false;
            }
          } else {
            faro.api.pushError(
              new Error("Fetching Finicity Connect URL Error"),
              { context: response },
            );
          }
          setIsLoading(false);
        } catch (error) {
          console.error("Error fetching Finicity Connect URL:", error);
          faro.api.pushError(
            new Error("Network Error Fetching Finicity Connect URL"),
            { context: error },
          );
          setIsLoading(false);
        }
      }

      // Get Plaid Link token if Plaid is enabled and Connect URL is not received
      if (
        session.toggles.plaidEnabled &&
        !connectUrlReceived &&
        !props.plaidRedirect
      ) {
        setIsLoading(true);
        let mounted = true;
        let oprUrl = "";
        if (url) {
          oprUrl = appKey
            ? "?redirectUri=" + url + "/" + appKey
            : "?redirectUri=" + url;
        } else {
          let basePlaidRedirectUri = window.location.href.includes(
            "oauth_state_id=",
          )
            ? window.location.href.split("?")[0]
            : window.location.href;
          let plaidRedirectUri = basePlaidRedirectUri.includes(
            session.merchantId,
          )
            ? basePlaidRedirectUri
            : basePlaidRedirectUri + "/" + session.merchantId;
          oprUrl = appKey
            ? basePlaidRedirectUri.includes(appKey)
              ? "?redirectUri=" + basePlaidRedirectUri
              : "?redirectUri=" + basePlaidRedirectUri + "/" + appKey
            : "?redirectUri=" + plaidRedirectUri;
        }
        try {
          await vipApi.fetch("/api/plaid/link" + oprUrl).then((res) => {
            res.json().then((data) => {
              if (!mounted) return;
              if (res.ok) {
                setLinkToken(data.linkToken);
                localStorage.setItem("link_token", data.linkToken);
              }
              if (!res.ok) {
                faro.api.pushError(new Error("Plaid Link Token Error"), {
                  context: data,
                });
              }
              setIsLoading(false);
            });
          });
        } catch (error) {
          faro.api.pushError(new Error("Plaid Link Token Network Error"), {
            context: error,
          });
          setIsLoading(false);
        }
        return () => {
          mounted = false;
        };
      }
    })();

    if (!session.toggles.plaidEnabled && !session.toggles.finicityEnabled) {
      faro.api.pushEvent("Neither Plaid nor Finicity enabled");
      setIsLoading(false);
    }
  }, []);

  var buttons = isLoading ? (
    <Loading />
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {props.plaidProps && !connectUrl && (
        <Plaid
          {...props.plaidProps}
          linkToken={linkToken}
          enrollmentStep={props.isEnrollment}
          receivedRedirectUri={receivedRedirectUri}
          isPlaidRedirect={props.plaidRedirect}
          buttonText={
            verifyMsg ? `Continue To Verify Bank` : `Connect Your Bank Account`
          }
        />
      )}
      {props.finicityEnabled && connectUrl && (
        <Finicity
          {...props.finicityProps}
          enrollmentStep={props.isEnrollment}
          connectUrl={connectUrl}
          buttonText={
            verifyMsg ? `Continue To Verify Bank` : `Connect Your Bank Account`
          }
        />
      )}
      {verifyMsg && (
        <div className="funding-source" style={{ marginTop: "10px" }}>
          <Button onClick={() => doNotVerify()} fullWidth>
            <div className="bank-label click-do-not-verify">Do Not Verify</div>
          </Button>
        </div>
      )}
      {props.plaidDefault && props.isEnrollment && (
        <div className="text-center">
          <b>or</b>
        </div>
      )}
      {props.bankProps && props.showManualCTA && props.isEnrollment && (
        <Manual
          {...props.bankProps}
          plaidDefault={props.plaidDefault}
          enrollmentStep={props.isEnrollment}
        />
      )}
      {props.nobank && <CancelButton stage="Enrollment"></CancelButton>}
    </Box>
  );

  return (
    <>
      {!connectUrl && !linkToken && !props.isEnrollment ? (
        isLoading ? (
          <Loading />
        ) : (
          <StatusUpdateCard
            heading="Sorry!"
            body={
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
              >
                <Typography>
                  We are unable to add new accounts at this time.
                </Typography>
              </Box>
            }
            icon={<img src={lockClock} alt="Account Created Error"></img>}
          />
        )
      ) : (
        <>
          {props.plaidRedirect && (
            <Plaid
              {...props.plaidProps}
              linkToken={linkToken}
              enrollmentStep={props.isEnrollment}
              receivedRedirectUri={receivedRedirectUri}
              isPlaidRedirect={props.plaidRedirect}
            />
          )}
          {props.plaidRedirect && isLoading ? (
            <Loading />
          ) : (
            <Box>
              <Typography
                className={`funding-source-subheader ${props.nobank && "error"}`}
                sx={{
                  marginBottom: "32px",
                  textAlign: { xs: "left", md: "center" },
                  justifyContent: { xs: "flex-start", md: "center" },
                  fontWeight:
                    !verifyMsg && !props.nobank && session.isVipEnrolled
                      ? 700
                      : undefined,
                }}
              >
                {verifyMsg
                  ? "Complete the bank account verification and we might be able to provide you with a higher limit!"
                  : props.nobank
                    ? "Insufficient Funds"
                    : "Select a payment option"}
              </Typography>
              {props.nobank && (
                <div className="add-funding-source-zero-balance">
                  <img
                    src={zerobalanceIcon}
                    alt="Add Funding Source Error"
                    style={{ paddingTop: "5px", paddingBottom: "10px" }}
                  ></img>
                  <p>
                    Your VIP account has been created, however, the bank account
                    you are trying to connect has $0 balance. Please check the
                    account balance or connect a new bank account.
                  </p>
                </div>
              )}
              {buttons}
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default FundingSourceList;
